import {
  createAsyncThunk,
  createSelector,
  createSlice
} from '@reduxjs/toolkit'
import generateProfileApi from '../../apis/profile'

const sliceName = 'profile'
const selectors = {}
const thunks = {}
const profileApi = generateProfileApi()

selectors.selectUserFeatures = createSelector(
  (state) => state[sliceName].user,
  (user) => user?.features || {}
)

selectors.selectIsLoggedIn = createSelector(
  (state) => state[sliceName].user?.id,
  (userId) => userId && userId > 0
)

/**
 * @param {Object} [options] Modifiers
 * @param {boolean} [options.includeAccount=false] Should the account information be included
 * @param {number} [options.updateCurrent=false] Should the current profile information be maintained and just updated. By default we reset the profile state on fetch.
 */
thunks.fetch = createAsyncThunk(
  `${sliceName}/fetch`,
  async ({ includeAccount = false, updateCurrent = false } = {}, thunkAPI) => {
    const include = []
    if (includeAccount) {
      include.push('account')
    }
    return profileApi.read({ include })
  }
)

thunks.authWithToken = createAsyncThunk(
  `${sliceName}/authWithToken`,
  async ({ token, includeAccount = false }, thunkAPI) => {
    await profileApi.read()
    await profileApi.authWithToken(token)
    return thunkAPI.dispatch(thunks.fetch({ includeAccount }))
  }
)

thunks.authWithPassword = createAsyncThunk(
  `${sliceName}/authWithPassword`,
  async ({ login, password, upgradePresenter = false, includeAccount = false }, { dispatch, rejectWithValue }) => {
    try {
      await profileApi.authWithPassword(login, password, upgradePresenter)
    } catch (e) {
      const error = e.responseJSON?.error
      return rejectWithValue(error || e)
    }
    return dispatch(thunks.fetch({ includeAccount }))
  }
)

thunks.logout = createAsyncThunk(
  `${sliceName}/logout`,
  async () => {
    return profileApi.delete()
  }
)

const initialState = {
  account: {},
  participant: {},
  user: {}
}

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    manuallyUpdated (state, action) {
      const { account, participant, user } = action.payload
      state.account = account || state.account
      state.participant = participant || state.participant
      state.user = user || state.user
    }
  },
  extraReducers (builder) {
    builder
      .addCase(thunks.fetch.fulfilled, (state, action) => {
        const { account = {}, participant, user } = action.payload
        state.account = account
        state.participant = participant
        state.user = user
      })
      .addCase(thunks.fetch.rejected, (state, action) => {
        console.error('Failed loading profile', action)
        if (action.meta.arg?.updateCurrent) {
          return state
        } else {
          return initialState
        }
      })
      .addCase(thunks.fetch.pending, (state, action) => {
        if (action.meta.arg?.updateCurrent) {
          return state
        } else {
          return initialState
        }
      })
    builder.addCase(thunks.authWithToken.pending, _ => initialState)
    builder.addCase(thunks.authWithPassword.pending, _ => initialState)
    builder.addCase(thunks.logout.fulfilled, _ => initialState)
  }
})

const sliceExport = {
  name: sliceName,
  reducer: slice.reducer,
  ...slice.actions,
  ...selectors,
  ...thunks
}

export { sliceExport as slice }
